import React, { useState, useEffect, useRef } from 'react';
import { statusMeta, priorityMeta, userDisplay } from './tokens';

export function Avatar({ u, size = 22, imgSrc }) {
  const d = userDisplay(u);
  if (imgSrc) {
    return <img src={imgSrc} alt={d.name}
      style={{ width: size, height: size, borderRadius: 999, objectFit: 'cover', flexShrink: 0 }} />;
  }
  return (
    <span style={{
      width: size, height: size, background: d.color, color: '#fff', borderRadius: 999,
      fontSize: size * 0.42, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      fontWeight: 600, letterSpacing: -0.3, flexShrink: 0,
    }}>{d.initials}</span>
  );
}

export function Dot({ c, r = 999, s = 8 }) {
  return <span style={{ background: c, borderRadius: r, width: s, height: s, display: 'inline-block' }} />;
}

export function Chip({ children, color, bg, mono, className = '' }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded text-[11px] ${mono ? 'ox-mono' : ''} ${className}`}
      style={{
        color: color || 'var(--text-muted)',
        background: bg || 'var(--chip)',
        border: '1px solid var(--border)',
      }}
    >{children}</span>
  );
}

export function StatusPill({ s }) {
  const st = statusMeta(s);
  return (
    <span className="inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded text-[11px]"
      style={{ background: st.weak, color: st.text }}>
      <span style={{ width: 6, height: 6, borderRadius: 999, background: st.dot }} />
      {st.label}
    </span>
  );
}

export function PriChip({ p }) {
  const pr = priorityMeta(p);
  return (
    <Chip color={pr.hex} bg="transparent">
      <span style={{ width: 6, height: 6, borderRadius: 999, background: pr.hex }} />
      {pr.label}
    </Chip>
  );
}

export function PriBar({ p }) {
  const hex = priorityMeta(p).hex;
  return (
    <span
      aria-label={`priority-${p}`}
      style={{
        width: 5,
        alignSelf: 'stretch',
        background: hex,
        borderRadius: 3,
        marginRight: 12,
        boxShadow: `0 0 0 1px ${hex}, 0 0 6px ${hex}55`,
      }}
    />
  );
}

export function Track({ value = 0, color = 'var(--accent)' }) {
  return (
    <div className="ox-track">
      <div style={{ width: `${Math.min(100, Math.max(0, value))}%`, height: '100%', background: color, opacity: 0.85 }} />
    </div>
  );
}

export function Spark({ values, color = 'var(--accent)', w = 120, h = 28, fill = true }) {
  if (!values?.length) return null;
  const max = Math.max(...values); const min = Math.min(...values);
  const span = max - min || 1;
  const pts = values.map((v, i) => {
    const x = (i / (values.length - 1)) * (w - 2) + 1;
    const y = h - 2 - ((v - min) / span) * (h - 4);
    return [x, y];
  });
  const d = pts.map((p, i) => (i ? 'L' : 'M') + p[0].toFixed(1) + ' ' + p[1].toFixed(1)).join(' ');
  const area = d + ` L ${pts[pts.length - 1][0]} ${h} L ${pts[0][0]} ${h} Z`;
  return (
    <svg width={w} height={h} style={{ color, display: 'block' }}>
      {fill && <path d={area} className="ox-spark-area" />}
      <path d={d} className="ox-spark-line" />
    </svg>
  );
}

export function Donut({ segments, size = 120, thickness = 14, centerLabel, centerValue }) {
  const total = segments.reduce((s, x) => s + x.value, 0) || 1;
  const r = size / 2 - thickness / 2;
  const c = 2 * Math.PI * r;
  let off = 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size / 2} cy={size / 2} r={r} stroke="var(--surface-2)" strokeWidth={thickness} fill="none" />
      {segments.map((s, i) => {
        const len = (s.value / total) * c;
        const el = (
          <circle key={i} cx={size / 2} cy={size / 2} r={r} stroke={s.color} strokeWidth={thickness} fill="none"
            strokeDasharray={`${len} ${c - len}`} strokeDashoffset={-off}
            transform={`rotate(-90 ${size / 2} ${size / 2})`} strokeLinecap="butt" />
        );
        off += len;
        return el;
      })}
      {centerValue != null && (
        <>
          <text x="50%" y="48%" textAnchor="middle" fontSize={size * 0.18} fontWeight="600" fill="var(--text)" style={{ fontVariantNumeric: 'tabular-nums' }}>{centerValue}</text>
          {centerLabel && <text x="50%" y="62%" textAnchor="middle" fontSize="10" fill="var(--text-dim)">{centerLabel}</text>}
        </>
      )}
    </svg>
  );
}

export function AreaChart({ data, height = 140, color = 'var(--accent)' }) {
  const [w, setW] = useState(600);
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    setW(ref.current.clientWidth);
    const ro = new ResizeObserver(() => setW(ref.current.clientWidth));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  const max = Math.max(...data.map(d => d.v), 1);
  const pad = { l: 28, r: 8, t: 8, b: 18 };
  const iw = w - pad.l - pad.r, ih = height - pad.t - pad.b;
  const pts = data.map((d, i) => {
    const x = pad.l + (i / Math.max(1, data.length - 1)) * iw;
    const y = pad.t + ih - (d.v / max) * ih;
    return [x, y, d];
  });
  const line = pts.map((p, i) => (i ? 'L' : 'M') + p[0].toFixed(1) + ' ' + p[1].toFixed(1)).join(' ');
  const area = line + ` L ${pts[pts.length - 1][0]} ${pad.t + ih} L ${pts[0][0]} ${pad.t + ih} Z`;
  const yTicks = [0, Math.ceil(max / 2), max];
  return (
    <div ref={ref} style={{ width: '100%' }}>
      <svg width={w} height={height} style={{ color, display: 'block' }}>
        {yTicks.map((t, i) => {
          const y = pad.t + ih - (t / max) * ih;
          return (
            <g key={i}>
              <line x1={pad.l} x2={w - pad.r} y1={y} y2={y} stroke="var(--border)" strokeDasharray="2 3" />
              <text x={pad.l - 6} y={y + 3} fontSize="9" fill="var(--text-dim)" textAnchor="end">{t}</text>
            </g>
          );
        })}
        <path d={area} className="ox-spark-area" />
        <path d={line} className="ox-spark-line" strokeWidth="1.75" />
        {pts.map((p, i) => (
          <g key={i}>
            {i % Math.max(1, Math.floor(pts.length / 8)) === 0 && (
              <text x={p[0]} y={height - 4} fontSize="9" fill="var(--text-dim)" textAnchor="middle">{p[2].d}</text>
            )}
            <circle cx={p[0]} cy={p[1]} r="2.2" fill={color} />
          </g>
        ))}
      </svg>
    </div>
  );
}

export function EmptyState({ icon, title, description, action }) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-12 px-6">
      <div
        className="w-20 h-20 rounded-full flex items-center justify-center mb-5"
        style={{ background: 'var(--accent-weak)', color: 'var(--accent)' }}
      >
        {icon}
      </div>
      <div className="ox-h2 mb-2">{title}</div>
      {description && <div className="ox-page-sub mb-5 max-w-sm">{description}</div>}
      {action}
    </div>
  );
}

export function SectionCard({ title, subtitle, right, children, pad = true, className = '' }) {
  return (
    <div className={`ox-surface rounded-md ${className}`}>
      <div className="px-4 py-2.5 flex items-center justify-between" style={{ borderBottom: '1px solid var(--border)' }}>
        <div>
          <div className="text-[13px] font-semibold">{title}</div>
          {subtitle && <div className="text-[11.5px] ox-text-muted">{subtitle}</div>}
        </div>
        <div className="flex items-center gap-2">{right}</div>
      </div>
      <div className={pad ? 'p-4' : ''}>{children}</div>
    </div>
  );
}

export function Seg({ value, onChange, options }) {
  return (
    <div className="ox-seg">
      {options.map(o => (
        <button key={o.value} aria-pressed={value === o.value} onClick={() => onChange(o.value)}>
          {o.label}
          {o.count != null && <span className="ox-mono ox-text-dim ml-1">{o.count}</span>}
        </button>
      ))}
    </div>
  );
}

export function KpiBigCard({ label, value, delta, dir, color, spark, sparkColor, sub }) {
  const up = dir === 'up';
  const { Ic } = require('./icons');
  return (
    <div className="ox-surface rounded-md p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11.5px] ox-text-muted uppercase tracking-wider" style={{ letterSpacing: '.04em' }}>{label}</div>
        {delta != null && (
          <span className="ox-mono text-[10.5px] flex items-center gap-0.5 px-1.5 py-0.5 rounded"
            style={{ color: up ? 'var(--good)' : 'var(--danger)', background: up ? 'var(--good-weak)' : 'var(--danger-weak)' }}>
            {up ? Ic.arrowUp : Ic.arrowDn}{Math.abs(delta)}%
          </span>
        )}
      </div>
      <div className="ox-tnum font-semibold leading-none" style={{ fontSize: 38, letterSpacing: -0.8, color }}>{value}</div>
      {spark && <div className="mt-3"><Spark values={spark} color={sparkColor} w={260} h={36} /></div>}
      {sub && <div className="text-[11.5px] ox-text-muted mt-2">{sub}</div>}
    </div>
  );
}
